import { useState, type FormEvent } from 'react';

import Grid from '@mui/material/Grid';
import Stack from '@mui/material/Stack';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import Alert from '@mui/material/Alert';

import AuthWrapper1 from 'views/authentication/AuthWrapper1';
import AuthCardWrapper from 'views/authentication/AuthCardWrapper';
import { connectInstance, InstanceConnectError } from 'services/config/connectInstance';

/**
 * Native-only "enter your instance" screen. Shown by InstanceGate when no instance is selected.
 * On submit it runs the shared connect flow; on success the gate re-renders into the normal login flow.
 */
const InstanceConnect = () => {
  const [host, setHost] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [connecting, setConnecting] = useState(false);

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    setError(null);
    setConnecting(true);

    try {
      await connectInstance(host);
    } catch (err) {
      setError(err instanceof InstanceConnectError ? err.message : 'Something went wrong. Please try again.');
    } finally {
      setConnecting(false);
    }
  };

  return (
    <AuthWrapper1>
      <Grid
        container
        direction="column"
        justifyContent="flex-end"
        sx={{ minHeight: 'var(--safe-area-height)', backgroundColor: 'primary.main' }}
      >
        <Grid item xs={12}>
          <Grid container justifyContent="center" alignItems="center" sx={{ minHeight: 'var(--safe-area-height)' }}>
            <Grid item sx={{ m: { xs: 1, sm: 3 }, mb: 0 }}>
              <AuthCardWrapper>
                <Box component="form" onSubmit={handleSubmit}>
                  <Stack spacing={3}>
                    <Stack spacing={1}>
                      <Typography variant="h3">Connect to your instance</Typography>
                      <Typography variant="body2" color="text.secondary">
                        Enter your AtlasOne instance name to get started.
                      </Typography>
                    </Stack>

                    {error && <Alert severity="error">{error}</Alert>}

                    <TextField
                      autoFocus
                      fullWidth
                      label="Instance name"
                      placeholder="your-team"
                      helperText="e.g. atlas for atlas.atlasone.co"
                      value={host}
                      onChange={(event) => setHost(event.target.value)}
                      disabled={connecting}
                      autoCapitalize="none"
                      autoCorrect="off"
                    />

                    <Button type="submit" variant="contained" size="large" fullWidth disabled={connecting || !host.trim()}>
                      {connecting ? 'Connecting...' : 'Continue'}
                    </Button>
                  </Stack>
                </Box>
              </AuthCardWrapper>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </AuthWrapper1>
  );
};

export default InstanceConnect;
